import React, { forwardRef } from 'react'
import styled from 'styled-components'
import {
  compose, space, color, typography
} from 'styled-system'
import { inputs } from '@zuto/utilities'
import FormGroup from '../Form/FormGroup'
import FormLabel, { LabelVariants } from '../Form/FormLabel'
import ErrorMessage from '../Form/ErrorMessage'

type InputVariant = 'default'

type InputHTMLProps = React.InputHTMLAttributes<HTMLInputElement>

interface StyledInputProps extends InputHTMLProps {
  $variant: InputVariant
  name: string
}

export interface InputProps extends InputHTMLProps {
  /** Content of the error for the field */
  error?: string
  /** Used to link the label and the field */
  name: string
  /** Text to display above the field */
  label?: string
  /** SVG Icon to display inside the text field on the left */
  icon?: React.ReactElement<Record<string, unknown>>
  /** Enables the error styling on the input without displaying a message
   * This is not needed if passing in an `error`.
  */
  addErrorClass?: boolean
  /** Styling for label * */
  labelVariant?: LabelVariants
}

const InputWrapper = styled.span`
  svg {
    position: absolute;
    padding: 13px 12px;
    path {
        fill: ${(props) => props.theme.colors.neutrals[3]};
    }
    circle {
      stroke: ${(props) => props.theme.colors.neutrals[3]};
    }
  }
`

const StyledInput = styled.input<StyledInputProps>`
  ${inputs}
  ${compose(space, color, typography)}
`

const Input = forwardRef<HTMLInputElement, InputProps>(({
  error, name, label, icon, className, addErrorClass, value, labelVariant, ...props
}: InputProps, ref) => (
  <FormGroup className={className ?? ''}>
    {label && <FormLabel name={name} data-testid="input-label" labelVariant={labelVariant}>{label}</FormLabel>}
    <InputWrapper>
      {icon && React.cloneElement(icon, { 'data-testid': 'pre-input-icon' })}
      <StyledInput ref={ref} aria-disabled={props.disabled || undefined} {...props} className={`${(error || addErrorClass) ? 'error' : ''}`} aria-label={`label-${name}`} id={name} name={name} $variant="default" value={value} />
    </InputWrapper>
    {error && (
      <ErrorMessage>
        {error}
      </ErrorMessage>
    )}
  </FormGroup>
))

export default Input
